import type { GetBookingStepPageDataQuery, PaymentDataOnPaymentPageQuery } from '@eti/schema-types';
import { routes } from '../../constants/routesAndApisConstants';
import type { StepType } from '../types/queries/getBookingStepPageDataQuery/models';

export const getStepUrl = (stepNumber: number): string => {
  switch (stepNumber) {
    case 1:
      return routes.RESULT;
    case 2:
      return routes.STEP_TWO;
    case 3:
      return routes.STEP_THREE;
    case 4:
      return routes.STEP_FOUR;
    case 5:
      return routes.STEP_FIVE;
    case 6:
      return routes.STEP_SIX;
    case 7:
      return routes.STEP_SEVEN;
    default:
      return '';
  }
};

// When someone completes a step, it auto-proceeds to the next step, so a step is considered visited
// when the previous step is completed(or it is itself completed already).
export const isStepVisited = (currentStep: StepType, stepsArray: StepType[]) => {
  const lastCompletedStep = stepsArray.reverse().find((step: StepType) => step.completed);
  stepsArray.reverse();

  return currentStep.stepNumber <= ((lastCompletedStep && lastCompletedStep.stepNumber + 1) || 0);
};

/*
 * In order to align with the backend we have to adapt the stepNumber field of the stepIndicator.
 * We are adding 2 to every stepNumber, as it starts from 0 according to the backend implementation
 * and finally, we are put two hardcoded steps (Result and Payment).
 * If we will have a backend fix we should revisit the following function.
 */
export const stepIndicatorDataAdapter = <
  T extends GetBookingStepPageDataQuery | PaymentDataOnPaymentPageQuery | undefined,
>(
  bookingStepPageData: T,
  resultPageTitle: string,
  paymentPageTitle: string,
  isPaymentPage: boolean = false,
) => {
  if (!bookingStepPageData) {
    return undefined;
  }

  const updatedBookingStepPageData = structuredClone(bookingStepPageData);

  const updatedStepIndicator =
    bookingStepPageData.selectedTrip?.dynamicBookingFlow?.stepIndicators.map((stepIndicator) => {
      return isPaymentPage
        ? {
            ...stepIndicator,
            stepNumber: stepIndicator.stepNumber + 2,
            completed: true,
          }
        : {
            ...stepIndicator,
            stepNumber: stepIndicator.stepNumber + 2,
          };
    }) ?? [];

  updatedStepIndicator.unshift({
    stepNumber: 1,
    name: resultPageTitle,
    completed: true,
    __typename: 'Step',
  });

  updatedStepIndicator.push({
    stepNumber: updatedStepIndicator.length + 1,
    name: paymentPageTitle,
    completed: false,
    __typename: 'Step',
  });

  if (
    updatedBookingStepPageData.selectedTrip &&
    updatedBookingStepPageData.selectedTrip.dynamicBookingFlow
  ) {
    updatedBookingStepPageData.selectedTrip.dynamicBookingFlow.stepIndicators =
      updatedStepIndicator;
  }

  return updatedBookingStepPageData;
};
